import { Box, Skeleton, Table, Text } from "@mantine/core";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import useSwr from "swr";
import { LedgerApi } from "../../../apis";
import { IIncomeStatementReport, IJournalResponse } from "../../../apis/ledger";
import { message } from "../../../utils/Message";
import { Amt, money } from "./report-format";
import { saveBlob } from "./report-download";
import { periodBounds, periodKey, useReportPeriod } from "./report-period";
import { ReportShell, UnmappedSection } from "./ReportShell";
import classes from "./ReportShell.module.css";
import { ListStates, resolveListStates } from "../../../components/list";

const COLS = 3;
/** Сарын бичилтийг нэг хүсэлтээр задлах дээд хязгаар (backend limit ≤ 100). */
const EXPLAIN_LIMIT = 100;

/** Орлогын тайлан (SPRINT-05 §6.3) — Орлого хасах зардал; тухайн сар ба оны эхнээс. */
export const LedgerIncomeStatementPage = () => {
  const { t } = useTranslation(["billing", "common"]);
  const [period, setPeriod] = useReportPeriod();
  const [exporting, setExporting] = useState<null | "excel" | "pdf">(null);
  const bounds = periodBounds(period);

  const { data, isLoading, error } = useSwr<IIncomeStatementReport>(
    `ledger.report.incomeStatement.${periodKey(period)}`,
    async () => await LedgerApi.getIncomeStatement(period),
    { revalidateOnFocus: false },
  );

  const lines = data?.lines ?? [];
  const isEmpty = !data || data.accountCount === 0 || lines.length === 0;
  const abnormal = !!data && !isEmpty && (data.revenue < 0 || data.netProfit < 0);

  // Сарын дүн сөрөг үед шалтгааныг тухайн сарын бичилтийн source-оос тооцоолно.
  const { data: journal } = useSwr<IJournalResponse>(
    abnormal ? `ledger.report.incomeStatement.explain.${periodKey(period)}` : null,
    async () => await LedgerApi.getJournal({ page: 1, limit: EXPLAIN_LIMIT, dateFrom: bounds.from, dateTo: bounds.to }),
    { revalidateOnFocus: false },
  );

  const explain = useMemo(() => {
    if (!journal) return null;
    const by = journal.summary.bySource;
    const amendments = by.INVOICE_AMENDMENT ?? 0;
    const invoices = by.INVOICE ?? 0;
    // Дүнг зөвхөн бүх бичилт нэг хуудсанд багтсан үед тооцно.
    let up = 0;
    let down = 0;
    const complete = journal.entries.length === journal.summary.entries;
    if (complete) {
      for (const e of journal.entries) {
        if (e.sourceType !== "INVOICE_AMENDMENT") continue;
        if (/_DOWN$/.test(e.postingTemplate ?? "")) down += Number(e.totalDebit);
        else up += Number(e.totalDebit);
      }
    }
    return { amendments, invoices, up, down, complete };
  }, [journal]);

  const ytdOf = (code: string): number | null => {
    if (!data) return null;
    if (code === "SALES_REVENUE") return data.ytdRevenue;
    if (code === "GROSS_PROFIT") return data.ytdGrossProfit;
    if (code === "NET_PROFIT") return data.ytdNetProfit;
    // Зардлын мөр бүрийн оны эхнээсийн дүнг API өгдөггүй; нийт зардал 0 бол мөр бүр 0.
    return data.ytdExpense === 0 ? 0 : null;
  };

  const runExport = async (format: "excel" | "pdf") => {
    setExporting(format);
    try {
      if (format === "excel") {
        saveBlob(await LedgerApi.exportReportExcel({ report: "income-statement", ...period }), `income-statement-${periodKey(period)}.xlsx`);
      } else {
        const { url } = await LedgerApi.exportReportPdf({ report: "income-statement", ...period });
        window.open(url, "_blank");
      }
    } catch (err) {
      message.error((err as Error).message);
    } finally {
      setExporting(null);
    }
  };

  const monthLabel = t(`billing:ledger.report.month.${period.month}`);
  const ytdUnknown = !!data && data.ytdExpense !== 0;

  const listStates = resolveListStates(
    <ListStates loading={isLoading} empty={isEmpty} columns={COLS} />
  );

  return (
    <ReportShell
      title={t("billing:ledger.report.incomeStatementTitle")}
      subtitle={
        <>
          {t("billing:ledger.report.incomeSubtitle", { year: period.year, month: monthLabel, ytd: money(data?.ytdNetProfit ?? 0) })}
          {" · "}
          <Link to={`/ledger/reports/balance-sheet?period=${periodKey(period)}`} className={classes.linkBtn}>
            {t("billing:ledger.report.toBalanceSheet")}
          </Link>
        </>
      }
      error={error}
      period={period}
      onPeriodChange={setPeriod}
      onExport={{
        onExcel: () => runExport("excel"),
        onPdf: () => runExport("pdf"),
        excelLoading: exporting === "excel",
        pdfLoading: exporting === "pdf",
      }}
    >
      <Box className={classes.card}>
        <Box className={classes.wrap}>
          {listStates ?? (
          <Table className={classes.table} withRowBorders={false} horizontalSpacing={0} verticalSpacing={0}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t("billing:ledger.report.colLine")}</Table.Th>
                <Table.Th className={classes.num}>{monthLabel}</Table.Th>
                <Table.Th className={classes.num}>{t("billing:ledger.report.colYtd")}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {lines.map((l) => {
                  const ytd = ytdOf(l.lineCode);
                  return (
                    <Table.Tr key={l.lineCode} className={l.isSubtotal ? classes.totalRow : classes.indent}>
                      <Table.Td>{l.lineName}</Table.Td>
                      <Table.Td className={classes.num}>
                        <Amt value={l.amount} signed strong={l.isSubtotal} />
                      </Table.Td>
                      <Table.Td className={classes.num}>
                        {ytd === null ? <Text span className={classes.muted}>·</Text> : <Amt value={ytd} signed strong={l.isSubtotal} />}
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
            </Table.Tbody>
          </Table>
          )}
        </Box>
        {ytdUnknown && <Box className={classes.cardFoot}>{t("billing:ledger.report.ytdLinesUnknown", { amount: money(data!.ytdExpense) })}</Box>}
      </Box>

      {abnormal && explain && (explain.amendments > 0 || explain.invoices > 0) && (
        <Box className={classes.explain}>
          <Text span className={classes.explainTitle}>
            {t("billing:ledger.report.negativeTitle", { month: monthLabel })}
          </Text>
          <Text span>
            {t("billing:ledger.report.negativeBody", { invoices: explain.invoices, amendments: explain.amendments })}
            {explain.complete && explain.amendments > 0 && (
              <>
                {" "}
                {t("billing:ledger.report.negativeAmounts", { up: money(explain.up), down: money(explain.down) })}
              </>
            )}{" "}
            <Link
              to={`/ledger/journal?from=${bounds.from}&to=${bounds.to}${explain.amendments > 0 && explain.invoices === 0 ? "&tab=INVOICE_AMENDMENT" : ""}`}
              className={classes.linkBtn}
            >
              {t("billing:ledger.report.viewInJournal")}
            </Link>
          </Text>
        </Box>
      )}

      <UnmappedSection rows={data?.unmapped ?? []} />
    </ReportShell>
  );
};

export default LedgerIncomeStatementPage;
